import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import { TNtfyNew } from '../../_types/ntfy';
import { useDispatch } from 'react-redux';
import { NtfyReport } from '../../features/store/ntfy.slice';
import { IUser } from '../../_types/user';
import {
  CircularProgress,
  Grid,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import StyledTableRow from '../StyledTableRow/StyledTableRow';
import { FetchContext } from '../../features/contexts/fetch';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import { ConfirmByTypingDialog } from '../ConfirmByTypingDialog/ConfirmByTypingDialog';
import { AssignUserPermissionsDialog } from '../AssignUserPermissionsDialog/AssignUserPermissionsDialog';
import { ChangeUserPasswordDialog } from '../ChangeUserPasswordDialog/ChangeUserPasswordDialog';
import { ChangeUserEmailDialog } from '../ChangeUserEmailDialog/ChangeUserEmailDialog';
import { PermissionSelection } from '../PermissionToggleList/PermissionToogleList';

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
  isDate?: boolean;
  humanize?: boolean;
  width: number | string;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'email',
    numeric: false,
    disablePadding: true,
    label: 'E-mail',
    width: 150,
  },
  {
    id: 'given_name',
    numeric: false,
    disablePadding: true,
    label: 'First Name',
    width: 100,
  },
  {
    id: 'family_name',
    numeric: false,
    disablePadding: true,
    label: 'Last Name',
    width: 150,
  },
  {
    id: 'logins_count',
    numeric: true,
    disablePadding: true,
    label: 'Logins',
    width: 75,
  },
  {
    id: 'created_at',
    numeric: false,
    disablePadding: true,
    isDate: true,
    humanize: false,
    label: 'Created At',
    width: 175,
  },
  {
    id: 'last_login',
    numeric: false,
    disablePadding: true,
    isDate: true,
    humanize: true,
    label: 'Latest Login',
    width: 175,
  }
];

interface RowAction {
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean | ((row: any) => boolean);
  onClick: (row: any) => void | Promise<void>;
  color?: string;
}

const UsersTable: React.FC = (): React.JSX.Element => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<IUser | null>(null);
  const [assignUserPermissionsDialogOpen, setAssignUserPermissionsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [changeUserPasswordDialogOpen, setChangeUserPasswordDialogOpen] = useState(false);
  const [changingUserPassword, setChangingUserPassword] = useState(false);
  const [changeUserEmailDialogOpen, setChangeUserEmailDialog] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);
  const dispatch = useDispatch<any>();

  const fetchContext = useContext(FetchContext);

  const actions: readonly RowAction[] = [
    {
      label: "Assign Permissions",
      icon: <ChecklistIcon />,
      onClick: (row) => {
        setAssignUserPermissionsDialogOpen(true);
        handleCloseMenu();
      },
    },
    {
      'label': '-',
      onClick: (row) => {
      },
    },
    /*
    {
      label: "Change Email",
      onClick: (row) => {
        setChangeUserEmailDialog(true);
        handleCloseMenu();
      },
    },
    */
    {
      label: "Change Password",
      onClick: (row) => {
        setChangeUserPasswordDialogOpen(true);
        handleCloseMenu();
      },
    },
    {
      'label': '-',
      onClick: (row) => {
      },
    },
    {
      label: "Delete",
      icon: <DeleteIcon />,
      disabled: (row) => row.status === "active", // example: only allow delete when disabled
      onClick: async (row) => {
        setDeleteDialogOpen(true);
        handleCloseMenu();
      },
      color: 'red',
    },
  ];

  const handleOpenMenu = (e: any, user: any) => {
    e.stopPropagation(); // don't trigger row onClick
    setMenuAnchor(e.currentTarget);
    console.log('setting selected row to', user);
    setSelectedRow(user);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleAction = async (action: RowAction) => {
    console.log('handle action');
    handleCloseMenu();
    if (!selectedRow) return;
    await action.onClick(selectedRow);
  };

  useEffect(() => {
    void (async () => {
      setBusy(true);
      setUsers([]);
      setCount(0);

      try {
        // @ts-ignore
        const { data } = await fetchContext.authAxios.get(
          `/users?page=${page}&perPage=${rowsPerPage}`,
        );

        setCount(data.total)
        setUsers(data.users);
      } catch (err: any) {
        const notification = TNtfyNew(
          err.message,
          'error',
          'long',
        );

        dispatch(NtfyReport(notification));
      }

      setBusy(false);
    })();
  }, [page, rowsPerPage]);

  const getFieldValue = (user: IUser, field: string, isDate: boolean, humanize: boolean) => {
    const value = user[field as keyof typeof user];

    return (
      <>
        {
          isDate ?
            (value ? (humanize ? moment(value).fromNow() : moment(value).format('MM/DD/YYYY')) : '') :
            value
        }
      </>
    );
  }

  const getTableRow = (user: IUser) => {
    return (
      <>
        {
          headCells.map(headCell => (
            <TableCell key={`${user.user_id}-${headCell.label}`}>
              {getFieldValue(user, headCell.id, headCell.isDate || false, headCell.humanize || false)}
            </TableCell>
          ))
        }
        <TableCell align="right" padding="checkbox">
          <Tooltip title="More actions">
            <IconButton
              size="small"
              aria-label="row actions"
              aria-controls={menuAnchor ? "row-actions-menu" : undefined}
              aria-haspopup="menu"
              aria-expanded={menuAnchor ? "true" : undefined}
              onClick={(e) => handleOpenMenu(e, user)}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </>
    );
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const confirmDelete = async () => {
    if (selectedRow) {
      console.log("Deleting row", selectedRow.name);
      setDeleting(true);

      try {
        // @ts-ignore
        await fetchContext.authAxios.delete(
          `/users/${selectedRow.user_id}`,
        );

        // get rid of the row that was selected for deletion
        setUsers(users.filter(user => user.user_id !== selectedRow.user_id));
      } catch (err: any) {
        const notification = TNtfyNew(
          err.message,
          'error',
          'long',
        );

        dispatch(NtfyReport(notification));
      }

      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleSaveUserPermissions = async (user: IUser | null, selectedPermissions: PermissionSelection, unselectedPermissions: PermissionSelection) => {
    if (user) {
      console.log(`saving permissions for ${user.user_id}:`, selectedPermissions);
      setSavingPermissions(true);

      const selected = { ... selectedPermissions };
      const unselected = { ... unselectedPermissions };

      /* remove permissions */
      if (Object.keys(unselected).length > 0) {
        try {
          // @ts-ignore
          await fetchContext.authAxios.delete(
            `users/${user.user_id}/permissions`,
            {
              data: unselected,
            }
          );
        } catch (err: any) {
          const notification = TNtfyNew(
            err.message,
            'error',
            'long',
          );

          dispatch(NtfyReport(notification));
        }
      }

      /* apply permissions */
      if (Object.keys(selected).length > 0) {
        try {
          // @ts-ignore
          await fetchContext.authAxios.post(
            `users/${user.user_id}/permissions`,
            selected,
          );
        } catch (err: any) {
          const notification = TNtfyNew(
            err.message,
            'error',
            'long',
          );

          dispatch(NtfyReport(notification));
        }
      }

      setSavingPermissions(false);
      setAssignUserPermissionsDialogOpen(false);
    }
  }

  const handleChangeUserPassword = async (userId: string, newPassword: string) => {
    if (userId && newPassword) {
      setChangingUserPassword(true);

      try {
        // @ts-ignore
        await fetchContext.authAxios.post(
          'change_user_password',
          {
            userId,
            newPassword,
          }
        );
      } catch (err: any) {
        const notification = TNtfyNew(
          err.message,
          'error',
          'long',
        );

        dispatch(NtfyReport(notification));
      }

      setChangingUserPassword(false);
      setChangeUserPasswordDialogOpen(false);
    }
  }

  const handleChangeUserEmail = (userId: string, newEmail: string) => {
    if (userId) {
      console.log('changing user email', userId);
      setChangingEmail(true);

      setChangingEmail(false);
      setChangeUserEmailDialog(false);
    }
  }

  return (
    <Paper
      elevation={3}
      sx={{ p: 3, borderRadius: 2,
        bgcolor: (theme) => theme.palette.background.paper,
        mx: 'auto', mt: 1
      }}>
      {users.length > 0 ? (
        <Grid container spacing={2} alignItems="center">
          {/* Title */}
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ ml: 1 }}>
              Users
            </Typography>
          </Grid>

          {/* Pagination */}
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              component="div"
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </Grid>

          {/* Table */}
          <Grid item xs={12}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {headCells.map((headCell, index) => (
                      <TableCell
                        key={headCell.id}
                        sx={{
                          width:
                            index !== headCells.length - 1
                              ? headCell.width
                              : 'auto',
                        }}
                      >
                        {headCell.label}
                      </TableCell>
                    ))}
                    <TableCell align="right" padding="checkbox" />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {users.map((user) => (
                    <StyledTableRow key={user.user_id}>
                      {getTableRow(user)}
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Row actions menu */}
              <Menu
                id="row-actions-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleCloseMenu}
                container={menuAnchor?.ownerDocument?.body}
                keepMounted
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {actions.map((action, idx) => {
                  const disabled =
                    typeof action.disabled === 'function'
                      ? !!(selectedRow && action.disabled(selectedRow))
                      : !!action.disabled;

                  return action.label !== '-' ? (
                    <MenuItem
                      key={idx}
                      onClick={() => handleAction(action)}
                      disabled={disabled}
                    >
                      {action.icon && (
                        <ListItemIcon sx={{ color: action.color || 'inherit' }}>
                          {action.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText sx={{ color: action.color || 'inherit' }}>
                        {action.label}
                      </ListItemText>
                    </MenuItem>
                  ) : (
                    <Divider key={idx} />
                  );
                })}
              </Menu>
            </TableContainer>
          </Grid>
        </Grid>
      ) : (
        busy && (
          <Typography align="center">
            <CircularProgress />
          </Typography>
        )
      )}

      {/* Assign User Permissions Dialog */}
      <AssignUserPermissionsDialog
        user={selectedRow}
        open={assignUserPermissionsDialogOpen}
        onClose={() => setAssignUserPermissionsDialogOpen(false)}
        onSave={handleSaveUserPermissions}
        loading={savingPermissions}
      />

      {/* Change User Email Dialog */}
      <ChangeUserEmailDialog
        userName={selectedRow?.name ?? ''}
        userId={selectedRow?.user_id ?? ''}
        email={selectedRow?.email ?? ''}
        open={changeUserEmailDialogOpen}
        onClose={() => setChangeUserEmailDialog(false)}
        onChange={handleChangeUserEmail}
        loading={changingUserPassword}
      />

      {/* Change User Password Dialog */}
      <ChangeUserPasswordDialog
        userName={selectedRow?.name ?? ''}
        userId={selectedRow?.user_id ?? ''}
        open={changeUserPasswordDialogOpen}
        onClose={() => setChangeUserPasswordDialogOpen(false)}
        onChange={handleChangeUserPassword}
        loading={changingUserPassword}
      />

      {/* Delete User Dialog */}
      <ConfirmByTypingDialog
        open={deleteDialogOpen}
        expectedText={selectedRow?.name ?? ''}
        title="Delete user"
        message={
          selectedRow && (
            <>
              This action cannot be undone. Deleting the account for{' '}
              <strong>{selectedRow.name}</strong> will remove it permanently.
            </>
          )
        }
        confirmLabel="Delete"
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </Paper>
  );
};

export default UsersTable;
